'use client';

import { TweetsBatchLoadResponseSchema } from '@sweetistics/contracts/tweets';
import { createUseTweetById, type UseTweetByIdHook } from '@sweetistics/tweets';
import { type QueryClient, QueryClientContext } from '@tanstack/react-query';
import { TRPCContext } from '@trpc/react-query/shared';
import type { ReactNode } from 'react';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { canRenderAnotherInlineTweet, INLINE_TWEET_MAX_DEPTH } from '@/components/tweet/inline-tweet-policy';
import { buildMentionProfileLookup } from '@/components/tweet/mention-profile';
import { TweetAvatar } from '@/components/tweet/TweetAvatar';
import { TweetBody } from '@/components/tweet/TweetBody';
import { TweetHeader } from '@/components/tweet/TweetHeader';
import { TweetErrorBoundary } from '@/domains/timeline/components/TweetErrorBoundary';
import { TweetNotFound } from '@/domains/timeline/components/TweetNotFound';
import { isTweetLike } from '@/domains/timeline/components/timeline-helpers';
import { env } from '@/env';
import { useIsClient } from '@/hooks/useIsClient';
import { primeTweetCacheFromTweets } from '@/lib/cache/tweet-cache';
import { registerDebugNamespace } from '@/lib/debug/registerDebugNamespace';
import type { LinkMetadata } from '@/lib/fetch-metadata';
import { isProductionRuntime } from '@/lib/runtime-flags';
import { isPlaywrightRuntime } from '@/lib/runtime-mode';
import { createTrpcClient, createTrpcQueryClient, trpc } from '@/lib/trpc/client';
import { wrapTrpcMutation } from '@/lib/trpc/mutation-utils';
import { TrpcProvider } from '@/lib/trpc/provider';
import { type ParseableTweet, type ParsedTweetContent, parseTweetText, type TweetMedia } from '@/lib/tweet-parser';
import { tryNormalizeTweetPayload } from '@/lib/twitter/normalize-tweet';
import { toLinkMetadataRecord } from '@/lib/url-metadata/normalize';
import { cn } from '@/lib/utils';
import { ensureDate } from '@/lib/utils/date-coercion';
import { isTruthyEnvFlag } from '@/lib/utils/env-flags';
import { isRecord, toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { TimelinePageResponse } from '@/lib/validation/api';
import type { Tweet as TweetType } from '@/types/tweet';
import { InlineTweetActionToolbar } from './InlineTweetActionToolbar';

interface InlineTweetProperties {
  className?: string;
  disableAutoSpacing?: boolean;
  fallbackUrl?: string;
  tweetId: string;
  visitedIds?: ReadonlySet<string>;
}

export type TweetData = TweetType;

type UseTweetByIdOptions = Parameters<UseTweetByIdHook>[1];
type SharedQueryClient = QueryClient;
type TimelineProfilesMap = TimelinePageResponse['profiles'];

const SESSION_EXPIRED_MESSAGE = 'Your session expired. Sign in again to view this tweet.';
const INCOMPLETE_TWEET_DATA_MESSAGE =
  "We received incomplete data from Twitter, so this tweet can't be rendered right now.";

const NormalizationErrorIssuesSchema = z.object({ issues: z.unknown().optional() }).passthrough();
const NormalizationErrorMessageSchema = z.object({ message: z.string().optional() }).passthrough();

const isTweetNormalizationSchemaError = (candidate: unknown): boolean => {
  if (candidate instanceof Error && candidate.name === 'ZodError') {
    return true;
  }

  if (!candidate || typeof candidate !== 'object') {
    return false;
  }

  const { issues } = candidate as { issues?: unknown };
  return Array.isArray(issues);
};

const deriveInlineTweetErrorMessage = (candidate: unknown): string | undefined => {
  if (!candidate) {
    return;
  }

  if (typeof candidate === 'string') {
    return candidate;
  }

  if (typeof candidate !== 'object') {
    return;
  }

  const raw = candidate as {
    message?: unknown;
    data?: {
      httpStatus?: number;
      code?: string;
      timelineError?: { httpStatus?: number; code?: string; message?: string } | null;
    };
  };

  const normalizedMessage = typeof raw.message === 'string' ? raw.message : undefined;
  const httpStatus = raw.data?.httpStatus ?? raw.data?.timelineError?.httpStatus;
  const trpcCode = raw.data?.code ?? raw.data?.timelineError?.code;

  if (httpStatus === 401 || trpcCode === 'UNAUTHORIZED') {
    return SESSION_EXPIRED_MESSAGE;
  }

  if (
    normalizedMessage?.toLowerCase().includes('twitter access expired') ||
    normalizedMessage?.toLowerCase().includes('authentication required') ||
    normalizedMessage?.toLowerCase().includes('failed to fetch tweets by ids')
  ) {
    return SESSION_EXPIRED_MESSAGE;
  }

  if (raw.data?.timelineError?.message) {
    return raw.data.timelineError.message;
  }

  return normalizedMessage;
};

const serializeUnknownErrorMessage = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Error) {
    return value.message;
  }
  try {
    const serialized = JSON.stringify(value);
    return serialized === '{}' ? String(value) : serialized;
  } catch {
    return String(value);
  }
};

const toInlineTweetQueryError = (value: unknown): InlineTweetDebugSnapshot['query']['error'] => {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: typeof value.stack === 'string' ? value.stack : null,
    };
  }
  return {
    name: 'UnknownError',
    message: serializeUnknownErrorMessage(value),
    stack: null,
  };
};

export function useTweetById(tweetId: string | null, options: UseTweetByIdOptions = {}) {
  const queryClient = useInlineTweetQueryClient();
  const batchLoadTweetsMutation = wrapTrpcMutation(
    trpc.tweets.batchLoadTweets.useMutation({
      trpc: { abortOnUnmount: true },
    }),
    (response) => response
  );

  const batchLoadTweets = useCallback(
    async (ids: readonly string[]) => {
      // Reuse the shared batch loader so the hook stays platform-agnostic while we
      // keep the tRPC wiring (auth, batching, response metadata) in this module.
      if (!isProductionRuntime()) {
        registerDebugNamespace('inline-tweet-batch-load', {
          phase: 'start',
          ids: [...ids],
        });
      }
      try {
        const response = await batchLoadTweetsMutation.mutateAsync({ ids: [...ids] });
        const parsedResponse = TweetsBatchLoadResponseSchema.safeParse(response);
        if (!parsedResponse.success) {
          if (!isProductionRuntime()) {
            registerDebugNamespace('inline-tweet-batch-load', {
              phase: 'parse-error',
              ids: [...ids],
              issues: parsedResponse.error.issues,
            });
          }
          throw parsedResponse.error;
        }
        if (!isProductionRuntime()) {
          registerDebugNamespace('inline-tweet-batch-load', {
            phase: 'success',
            ids: [...ids],
            tweetIds: Object.keys(parsedResponse.data.tweets),
          });
        }
        return {
          tweets: parsedResponse.data.tweets,
        };
      } catch (mutateError) {
        if (!isProductionRuntime()) {
          const errorPayload =
            mutateError instanceof Error
              ? { name: mutateError.name, message: mutateError.message }
              : { message: String(mutateError) };
          registerDebugNamespace('inline-tweet-batch-load', {
            phase: 'error',
            ids: [...ids],
            error: errorPayload,
          });
        }
        throw mutateError;
      }
    },
    [batchLoadTweetsMutation]
  );

  const hook = useMemo(() => {
    const factory = createUseTweetById({
      useQueryClient: () => queryClient,
      batchLoadTweets,
    });
    return ((nextTweetId, nextOptions) => factory(nextTweetId, nextOptions)) as UseTweetByIdHook;
  }, [batchLoadTweets, queryClient]);

  return hook(tweetId, options);
}

function InlineTweet({
  tweetId,
  className = '',
  disableAutoSpacing = false,
  fallbackUrl: _fallbackUrl,
  visitedIds,
}: InlineTweetProperties) {
  const fallbackUrl = _fallbackUrl ?? `https://twitter.com/i/status/${tweetId}`;
  const canRender = canRenderAnotherInlineTweet(visitedIds);
  const hasHydrated = useIsClient();

  const visited = useMemo(() => {
    // Track previously rendered tweet ids so nested inline tweets cannot recurse forever.
    const base = visitedIds ? new Set(visitedIds) : new Set<string>();
    if (canRender) {
      base.add(tweetId);
    }
    return base;
  }, [visitedIds, tweetId, canRender]);

  const {
    data: tweet,
    isLoading,
    isError,
    isFetching,
    status,
    fetchStatus,
    failureCount,
    error,
  } = useTweetById(canRender ? tweetId : null, {
    enabled: canRender,
  });

  const normalizationErrorRef = useRef<unknown>(undefined);
  const queryClient = useInlineTweetQueryClient();
  const [mediaRefreshRequested, setMediaRefreshRequested] = useState(false);

  useEffect(() => {
    if (tweetId === null) {
      setMediaRefreshRequested(false);
      return;
    }
    setMediaRefreshRequested(false);
  }, [tweetId]);

  const normalizedTweet = useMemo<TweetType | null>(() => {
    if (!tweet) {
      normalizationErrorRef.current = undefined;
      return null;
    }
    const result = tryNormalizeTweetPayload(tweet, { suppressLogging: true });
    if (result.ok) {
      normalizationErrorRef.current = undefined;
      return result.tweet;
    }

    normalizationErrorRef.current = result.error;

    if (!isProductionRuntime()) {
      const parsedIssues = NormalizationErrorIssuesSchema.safeParse(result.error);
      const payload =
        result.error instanceof Error
          ? {
              name: result.error.name,
              message: result.error.message,
              issues: parsedIssues.success ? parsedIssues.data.issues : undefined,
            }
          : { value: result.error };
      registerDebugNamespace('inline-tweet-normalize-error', {
        tweetId,
        error: payload,
      });
    }

    return null;
  }, [tweet, tweetId]);

  const hasAttachmentMediaKeys =
    Array.isArray(normalizedTweet?.attachments?.mediaKeys) && normalizedTweet.attachments.mediaKeys.length > 0;
  const hasMediaItems = Array.isArray(normalizedTweet?.mediaItems) && normalizedTweet.mediaItems.length > 0;
  const shouldRefreshMedia = Boolean(
    normalizedTweet && hasAttachmentMediaKeys && !hasMediaItems && !mediaRefreshRequested
  );

  const mediaRefreshQuery = trpc.tweets.getTweet.useQuery(
    { tweetId, strategy: 'twitter-only' },
    {
      enabled: shouldRefreshMedia,
      staleTime: 0,
      gcTime: 5 * 60 * 1000,
    }
  );

  useEffect(() => {
    if (shouldRefreshMedia) {
      setMediaRefreshRequested(true);
    }
  }, [shouldRefreshMedia]);

  useEffect(() => {
    if (!mediaRefreshQuery.data) {
      return;
    }
    queryClient.setQueryData(['tweet', tweetId], mediaRefreshQuery.data);
  }, [mediaRefreshQuery.data, queryClient, tweetId]);

  const skeleton = (
    <div
      className={cn(
        'mt-3 mb-2 flex items-center gap-2 rounded-xl border border-border bg-card/60 p-3 animate-pulse',
        disableAutoSpacing && 'mt-0 mb-0',
        className
      )}
    >
      <div className="h-8 w-8 rounded-full bg-muted" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-1/3 rounded bg-muted" />
        <div className="h-3 w-2/3 rounded bg-muted" />
        <div className="h-3 w-1/2 rounded bg-muted" />
      </div>
    </div>
  );

  const memoizedParse = useMemo(() => {
    if (!normalizedTweet) {
      return {
        parseable: null as ParseableTweet | null,
        parsedHtml: '',
        cardUrls: [] as ReturnType<typeof parseTweetText>['cardUrls'],
        inlineTweetUrls: [] as ReturnType<typeof parseTweetText>['inlineTweetUrls'],
        quotedTweetId: undefined as string | undefined,
        longFormHint: false,
        metadataRecord: undefined as Record<string, LinkMetadata> | undefined,
      };
    }

    const parsedMetadataRecord = toLinkMetadataRecord(normalizedTweet.metadata ?? undefined) ?? undefined;

    const parseable: ParseableTweet = {
      text: normalizedTweet.text,
    };
    if (normalizedTweet.entities) {
      parseable.entities = normalizedTweet.entities;
    }
    if (normalizedTweet.mediaItems && normalizedTweet.mediaItems.length > 0) {
      const mediaItems: TweetMedia[] = [];
      for (const item of normalizedTweet.mediaItems) {
        const base: TweetMedia = {
          mediaKey: item.mediaKey,
        };
        if (item.type) {
          base.type = item.type;
        }
        if (typeof item.url === 'string' && item.url.length > 0) {
          base.url = item.url;
        }
        if (typeof item.width === 'number') {
          base.width = item.width;
        }
        if (typeof item.height === 'number') {
          base.height = item.height;
        }
        mediaItems.push(base);
      }
      parseable.mediaItems = mediaItems;
    }
    if (parsedMetadataRecord) {
      parseable.metadata = parsedMetadataRecord;
    }

    let parsed: ParsedTweetContent;
    try {
      parsed = parseTweetText(parseable);
    } catch (parseError) {
      // Temporary debug instrumentation: capture parse failures until root cause is fixed.
      const payload = {
        tweetId: normalizedTweet.id,
        hasEntities: Boolean(normalizedTweet.entities),
        hasMediaItems: Array.isArray(normalizedTweet.mediaItems) && normalizedTweet.mediaItems.length > 0,
        hasMetadata: parsedMetadataRecord ? Object.keys(parsedMetadataRecord).length > 0 : false,
      };
      registerDebugNamespace('inline-tweet-parse-error', {
        ...payload,
        error:
          parseError instanceof Error
            ? { name: parseError.name, message: parseError.message }
            : { message: String(parseError) },
        parseable,
      });
      parsed = {
        html: normalizedTweet.text ?? '',
        cardUrls: [],
        inlineTweetUrls: [],
      };
    }
    const derivedQuotedTweetId =
      normalizedTweet.referencedTweets?.find((reference) => reference.type === 'quoted')?.id ?? undefined;
    const derivedLongFormHint = Boolean(normalizedTweet.noteTweet?.text?.length);

    return {
      parseable,
      parsedHtml: parsed.html,
      cardUrls: parsed.cardUrls,
      inlineTweetUrls: parsed.inlineTweetUrls,
      quotedTweetId: derivedQuotedTweetId,
      longFormHint: derivedLongFormHint,
      metadataRecord: parsedMetadataRecord,
    };
  }, [normalizedTweet]);

  const visitedIdsList = useMemo(() => [...visited], [visited]);

  const debugSnapshot = useMemo<InlineTweetDebugSnapshot>(
    () => ({
      timestamp: ensureDate(Date.now()),
      tweetId,
      canRender,
      disableAutoSpacing,
      visitedIds: visitedIdsList,
      query: {
        status,
        fetchStatus,
        isLoading,
        isFetching,
        isError,
        failureCount,
        hasData: Boolean(normalizedTweet),
        error: toInlineTweetQueryError(error),
      },
      tweetSummary: normalizedTweet
        ? {
            hasAuthor: Boolean(normalizedTweet.author),
            mediaItems: Array.isArray(normalizedTweet.mediaItems) ? normalizedTweet.mediaItems.length : 0,
            referencedTweetTypes: Array.isArray(normalizedTweet.referencedTweets)
              ? normalizedTweet.referencedTweets.map((reference) => reference.type)
              : [],
            quotedTweetId: memoizedParse.quotedTweetId ?? null,
            retweetedId:
              normalizedTweet.referencedTweets?.find((reference) => reference.type === 'retweeted')?.id ?? null,
          }
        : null,
      parseSummary: {
        inlineTweetUrlCount: memoizedParse.inlineTweetUrls.length,
        cardUrlCount: memoizedParse.cardUrls.length,
        longFormHint: memoizedParse.longFormHint,
      },
    }),
    [
      canRender,
      disableAutoSpacing,
      error,
      failureCount,
      fetchStatus,
      isError,
      isFetching,
      isLoading,
      memoizedParse.cardUrls.length,
      memoizedParse.inlineTweetUrls.length,
      memoizedParse.longFormHint,
      memoizedParse.quotedTweetId,
      status,
      normalizedTweet,
      tweetId,
      visitedIdsList,
    ]
  );

  const mentionProfileLookup = useMemo(
    () => buildMentionProfileLookup(normalizedTweet ?? undefined) ?? undefined,
    [normalizedTweet]
  );

  if (!canRender) {
    // Depth cap keeps inline recursion sane—bump INLINE_TWEET_MAX_DEPTH only after UX review.
    return (
      <>
        <InlineTweetDebugProbe snapshot={debugSnapshot} />
        <div
          className={cn(
            'mt-3 mb-2 rounded-xl border border-border/60 bg-card/60 p-3 text-sm text-muted-foreground',
            disableAutoSpacing && 'mt-0 mb-0',
            className
          )}
        >
          <p className="font-medium text-foreground">Inline preview trimmed</p>
          <p className="mt-1">This thread already has {INLINE_TWEET_MAX_DEPTH} nested previews.</p>
          <a
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center text-primary hover:underline"
          >
            Open tweet on X →
          </a>
        </div>
      </>
    );
  }

  if (!hasHydrated || isLoading) {
    return (
      <>
        <InlineTweetDebugProbe snapshot={debugSnapshot} />
        {skeleton}
      </>
    );
  }

  const normalizationError = normalizationErrorRef.current;
  const normalizationErrorMessage = (() => {
    if (!normalizationError) {
      return;
    }
    if (isTweetNormalizationSchemaError(normalizationError)) {
      return INCOMPLETE_TWEET_DATA_MESSAGE;
    }
    if (normalizationError instanceof Error) {
      return toTrimmedNonEmptyString(normalizationError.message) ?? undefined;
    }
    const messageCandidate = NormalizationErrorMessageSchema.safeParse(normalizationError);
    if (messageCandidate.success) {
      const candidate = toTrimmedNonEmptyString(messageCandidate.data.message);
      if (candidate) {
        return candidate;
      }
    }
    return;
  })();

  if (isError || !normalizedTweet || !memoizedParse.parseable) {
    const requestErrorMessage = toTrimmedNonEmptyString(deriveInlineTweetErrorMessage(error));
    const errorMessage = requestErrorMessage ?? normalizationErrorMessage;
    return (
      <>
        <InlineTweetDebugProbe snapshot={debugSnapshot} />
        <TweetNotFound tweetId={tweetId} message={errorMessage} />
      </>
    );
  }

  const { parsedHtml, cardUrls, inlineTweetUrls, quotedTweetId, longFormHint, metadataRecord } = memoizedParse;
  const tweetCreatedAt = normalizedTweet.createdAt ?? null;

  return (
    <>
      <InlineTweetDebugProbe snapshot={debugSnapshot} />
      <div
        className={cn(
          'overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm',
          disableAutoSpacing ? '' : 'mt-3 mb-2',
          className
        )}
      >
        <div className="flex gap-3 p-3">
          {normalizedTweet.author ? (
            <TweetAvatar author={normalizedTweet.author} size="sm" />
          ) : (
            <div className="h-10 w-10 shrink-0 rounded-full bg-muted" />
          )}
          <div className="flex-1 min-w-0 space-y-2">
            {normalizedTweet.author && (
              <TweetHeader
                author={normalizedTweet.author}
                createdAt={tweetCreatedAt}
                tweetId={normalizedTweet.id}
                compact
              />
            )}
            <TweetBody
              parsedHtml={parsedHtml}
              mediaItems={normalizedTweet.mediaItems}
              cardUrls={cardUrls}
              inlineTweetUrls={inlineTweetUrls}
              quotedTweetId={quotedTweetId}
              metadata={metadataRecord}
              mediaMetadata={normalizedTweet.mediaMetadata}
              compact
              tweetId={normalizedTweet.id}
              longFormHint={longFormHint}
              renderQuotedFallback
              inlineTweetComponent={InlineTweet}
              inlineTweetVisitedIds={visited}
              mentionProfiles={mentionProfileLookup}
            />
            {/* Inline tweets should render the full action toolbar so nested content behaves like primary tweets. */}
            <InlineTweetActionToolbar tweetId={tweetId} tweet={normalizedTweet} />
          </div>
        </div>
      </div>
    </>
  );
}

export const populateTweetCache = (queryClient: SharedQueryClient, tweets: Record<string, unknown>) => {
  const visited = new Set<string>();
  const collected: TweetData[] = [];

  const upsert = (tweet: unknown) => {
    if (!tweet) {
      return;
    }

    // biome-ignore lint/complexity/useLiteralKeys: Required under noPropertyAccessFromIndexSignature.
    const tweetIdCandidate = isRecord(tweet) ? tweet['id'] : undefined;
    const candidateTweetId = typeof tweetIdCandidate === 'string' ? tweetIdCandidate : undefined;
    if (!isTweetLike(tweet)) {
      if (!isProductionRuntime()) {
        registerDebugNamespace('inline-tweet-cache', {
          phase: 'skip-invalid-tweet',
          tweetId: candidateTweetId,
          reasons: {
            // biome-ignore lint/complexity/useLiteralKeys: Required under noPropertyAccessFromIndexSignature.
            hasAuthorId: typeof (isRecord(tweet) ? tweet['authorId'] : undefined) === 'string',
            // biome-ignore lint/complexity/useLiteralKeys: Required under noPropertyAccessFromIndexSignature.
            hasCreatedAt: (isRecord(tweet) ? tweet['createdAt'] : undefined) instanceof Date,
          },
        });
      }
      return;
    }

    const tweetId = tweet.id;
    if (visited.has(tweetId)) {
      return;
    }

    visited.add(tweetId);
    collected.push(tweet);
    queryClient.setQueryData(['tweet', tweetId], tweet);

    // Inline embeds frequently reference quoted/retweeted tweets. Hydrate those ahead of time so
    // client components don't need a follow-up request when they expand nested content.
    upsert(tweet.quotedTweet ?? undefined);
    upsert(tweet.retweetedTweet ?? undefined);
  };

  for (const tweet of Object.values(tweets)) {
    upsert(tweet);
  }

  if (collected.length > 0) {
    if (!isProductionRuntime()) {
      const seededIds = collected.map((tweet) => tweet.id);
      console.info('[inline-tweet-cache] populated React Query cache', { seededIds, total: seededIds.length });
      registerDebugNamespace('inline-tweet-cache', {
        phase: 'prime',
        seededIds,
        total: seededIds.length,
        timestamp: ensureDate(Date.now()),
      });
    }
    primeTweetCacheFromTweets(collected).catch(() => {
      // Swallow cache warm-up errors; they’re best-effort hints.
    });
  }
};

export const populateProfileCache = (queryClient: SharedQueryClient, profiles: TimelineProfilesMap) => {
  for (const [id, profile] of Object.entries(profiles)) {
    queryClient.setQueryData(['profile', id], profile);
  }
};

interface InlineTweetDebugSnapshot {
  canRender: boolean;
  disableAutoSpacing: boolean;
  parseSummary: {
    inlineTweetUrlCount: number;
    cardUrlCount: number;
    longFormHint: boolean;
  };
  query: {
    status: 'pending' | 'error' | 'success';
    fetchStatus: 'idle' | 'fetching' | 'paused';
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    failureCount: number;
    hasData: boolean;
    error: { name: string; message: string; stack?: string | null } | null;
  };
  timestamp: Date;
  tweetId: string;
  tweetSummary: {
    hasAuthor: boolean;
    mediaItems: number;
    referencedTweetTypes: string[];
    quotedTweetId: string | null;
    retweetedId: string | null;
  } | null;
  visitedIds: string[];
}

function InlineTweetDebugProbe({ snapshot }: { snapshot: InlineTweetDebugSnapshot }) {
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    const debugEnabled =
      !isProductionRuntime() && isTruthyEnvFlag(env.NEXT_PUBLIC_ENABLE_RT_DEBUG) && !isPlaywrightRuntime();

    if (!debugEnabled) {
      return;
    }

    registerDebugNamespace('inline-tweet', snapshot);

    const { tweetId, query } = snapshot;
    const signature = [
      tweetId,
      query.status,
      query.fetchStatus,
      query.hasData ? '1' : '0',
      String(query.failureCount),
    ].join('|');

    if (lastSignatureRef.current !== signature) {
      lastSignatureRef.current = signature;
      const payload = { tweetId, query };
      const hasActiveRequest = query.hasData || query.isLoading || query.isFetching;
      const isStalled = query.isError || !hasActiveRequest;
      if (isStalled) {
        console.warn('[pulse-inline-tweet] query stalled', payload);
      } else if (query.hasData) {
        console.info('[pulse-inline-tweet] query hydrated', {
          tweetId,
          status: query.status,
          fetchStatus: query.fetchStatus,
        });
      }
    }
  }, [snapshot]);

  return null;
}

const MemoizedInlineTweet = memo(InlineTweet);

function InlineTweetFallback({
  error,
  reset,
  fallbackUrl,
}: {
  error: Error | null;
  reset: () => void;
  fallbackUrl: string;
}) {
  const errorMessage = error?.message?.trim();

  return (
    <div className="mt-3 mb-2 rounded-xl border border-border/60 bg-card p-3">
      <div className="space-y-3 text-sm text-muted-foreground">
        <p>{errorMessage && errorMessage.length > 0 ? errorMessage : 'We couldn’t display this tweet preview.'}</p>
        <div className="flex flex-wrap gap-3 text-xs">
          <button type="button" onClick={reset} className="text-primary hover:underline">
            Try again
          </button>
          <a href={fallbackUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            Open tweet on X
          </a>
        </div>
      </div>
    </div>
  );
}

type InlineTweetFallbackContext = {
  queryClient: QueryClient | null;
  trpcClient: ReturnType<typeof createTrpcClient> | null;
};

const INLINE_TWEET_FALLBACK_CONTEXT: InlineTweetFallbackContext = {
  queryClient: null,
  trpcClient: null,
};

const hasTrpcClient = (value: unknown): value is { client: ReturnType<typeof createTrpcClient> } =>
  typeof value === 'object' && value !== null && 'client' in value && value.client !== undefined;

function getInlineTweetFallbackContext(): {
  queryClient: QueryClient;
  trpcClient: ReturnType<typeof createTrpcClient>;
} {
  if (!INLINE_TWEET_FALLBACK_CONTEXT.queryClient) {
    INLINE_TWEET_FALLBACK_CONTEXT.queryClient = createTrpcQueryClient();
  }
  if (!INLINE_TWEET_FALLBACK_CONTEXT.trpcClient) {
    INLINE_TWEET_FALLBACK_CONTEXT.trpcClient = createTrpcClient();
  }

  const { queryClient, trpcClient } = INLINE_TWEET_FALLBACK_CONTEXT;
  if (!queryClient) {
    throw new Error('Failed to initialize inline tweet fallback context');
  }
  if (!trpcClient) {
    throw new Error('Failed to initialize inline tweet fallback context');
  }
  return { queryClient, trpcClient };
}

function InlineTweetProviderBridge({ children }: { children: ReactNode }) {
  const parentQueryClient = useContext(QueryClientContext);
  const trpcContextValue: unknown = useContext(TRPCContext);
  const trpcClientFromContext = hasTrpcClient(trpcContextValue) ? trpcContextValue.client : undefined;

  const fallback = getInlineTweetFallbackContext();
  const queryClient = parentQueryClient ?? fallback.queryClient;
  const trpcClient = trpcClientFromContext ?? fallback.trpcClient;

  return (
    <TrpcProvider client={trpcClient} queryClient={queryClient} includeQueryClientProvider>
      {children}
    </TrpcProvider>
  );
}

function useInlineTweetQueryClient(): QueryClient {
  const parentQueryClient = useContext(QueryClientContext);
  if (parentQueryClient) {
    return parentQueryClient;
  }
  const fallback = getInlineTweetFallbackContext();
  return fallback.queryClient;
}

export default function InlineTweetWithErrorBoundary(properties: InlineTweetProperties) {
  const fallbackUrl = properties.fallbackUrl ?? `https://twitter.com/i/status/${properties.tweetId}`;

  return (
    <TweetErrorBoundary
      fallback={({ error, reset }) => <InlineTweetFallback error={error} reset={reset} fallbackUrl={fallbackUrl} />}
    >
      <InlineTweetProviderBridge>
        <MemoizedInlineTweet {...properties} />
      </InlineTweetProviderBridge>
    </TweetErrorBoundary>
  );
}
